import React from 'react';
import { LEGAL_ENTITY, PRODUCT_NAME, CONTACT_EMAIL, WEBSITE_URL } from './constants';
import { getLegalStyles } from './legalStyles';

function MailLink({ styles }) {
    return (
        <a href={`mailto:${CONTACT_EMAIL}`} className={styles.link}>
            {CONTACT_EMAIL}
        </a>
    );
}

/**
 * Refund Policy body for /refund-policy.
 * @param {{ variant?: 'page' | 'modal' }} props
 */
export function RefundPolicyContent({ variant = 'page' }) {
    const s = getLegalStyles(variant);

    return (
        <div className="space-y-8 leading-relaxed">
            <section className={s.section}>
                <h2 className={s.h2}>1. Overview</h2>
                <p className={s.p}>
                    This Refund Policy applies to paid subscriptions for <strong>{PRODUCT_NAME}</strong>{' '}
                    operated by <strong>{LEGAL_ENTITY}</strong>. Payments are processed through our
                    secure payment system. By subscribing, you agree to this policy in addition to
                    our{' '}
                    <a href="/terms-of-service" className={s.link}>
                        Terms of Service
                    </a>
                    .
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>2. Subscription Fees</h2>
                <p className={s.p}>
                    Subscription fees are billed in advance on a recurring basis according to the
                    plan you select. Except where required by applicable law, all fees are{' '}
                    <strong>non-refundable</strong>, including after cancellation mid-cycle.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>3. Free Trials & Promotions</h2>
                <p className={s.p}>
                    If we offer a free trial or promotional period, you may cancel before the trial
                    ends to avoid charges. Once a paid subscription begins, standard billing and this
                    Refund Policy apply.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>4. Cancellations</h2>
                <p className={s.p}>
                    You may cancel at any time through the billing portal in your {PRODUCT_NAME}{' '}
                    account Settings or by contacting <MailLink styles={s} />. After
                    cancellation, you retain access through the end of your current paid billing
                    period. We do not refund unused time, unused features, or plan downgrades.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>5. No Partial Refunds</h2>
                <p className={s.p}>
                    We do not provide prorated or partial refunds for unused portions of a billing
                    cycle, early termination, account suspension for policy violations, or
                    discontinued features.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>6. Billing Errors & Disputes</h2>
                <p className={s.p}>
                    If you believe you were charged in error (duplicate charge, wrong plan, or
                    unauthorized transaction), contact us within thirty (30) days at{' '}
                    <MailLink styles={s} />. We will review and, where appropriate, issue a refund to
                    the original payment method. Chargebacks should be a last resort; contact us
                    first so we can resolve billing issues directly.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>7. Service Issues</h2>
                <p className={s.p}>
                    If you experience technical problems, contact support promptly. We will use
                    reasonable efforts to resolve issues but do not guarantee refunds for outages,
                    bugs, wallet or device compatibility issues, or factors outside our control.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>8. Exceptional Refunds</h2>
                <p className={s.p}>
                    {LEGAL_ENTITY} may issue refunds at its sole discretion in exceptional
                    circumstances not covered above. Any approved refund is processed to the original
                    payment method and may take several business days to appear.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>9. Changes</h2>
                <p className={s.p}>
                    We may update this Refund Policy by posting a revised version with a new effective
                    date. Continued use of paid features after the effective date constitutes
                    acceptance, except where prohibited by law.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>10. Contact</h2>
                <p className={s.p}>
                    Questions about billing or refunds: <MailLink styles={s} />
                    <br />
                    Website:{' '}
                    <a href={WEBSITE_URL} className={s.link} target="_blank" rel="noopener noreferrer">
                        {WEBSITE_URL}
                    </a>
                </p>
            </section>

            <p className={s.disclaimer}>
                This policy is provided for general informational purposes and does not constitute
                legal advice.
            </p>
        </div>
    );
}
